"use client";

import type { AuthChangeEvent, User } from "@supabase/supabase-js";

import { createContext, useContext, useEffect } from "react";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export type AuthEventListener = (
  event: AuthChangeEvent,
  user: User | null,
) => void;

export interface AuthContextValue {
  /** `loading` until Supabase reports the initial session. */
  status: AuthStatus;
  user: User | null;
  /** Sign out (the language preference is kept). */
  signOut: () => Promise<{ ok: boolean }>;
  /** Listen to raw Supabase auth events (e.g. `TOKEN_REFRESHED`). */
  subscribe: (listener: AuthEventListener) => () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** The signed-in user, from the app-wide `<AuthProvider>`. */
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (!value) throw new Error("useAuth() must be used inside <AuthProvider>");

  return value;
}

/** Run `listener` for every auth event; pass a stable (memoized) function. */
export function useAuthEvent(listener: AuthEventListener): void {
  const { subscribe } = useAuth();

  useEffect(() => subscribe(listener), [subscribe, listener]);
}

/** A display name for the user, from whatever their provider gave us. */
export function getUserDisplayName(user: User): string | null {
  const metadata = user.user_metadata ?? {};

  for (const key of ["full_name", "name", "nickname", "user_name"]) {
    const value = metadata[key];

    if (typeof value === "string" && value.trim()) return value.trim();
  }

  return null;
}
